'use client';

import { Component, ErrorInfo, type ReactNode } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { ErrorBoundaryFallback } from './ErrorBoundaryFallback';

interface MainContentErrorBoundaryProps {
  children: ReactNode;
}

interface MainContentErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

/**
 * ErrorBoundary specifically designed to wrap the main content area
 * while preserving navigation elements (top nav, sidebar, footer)
 */
export class MainContentErrorBoundary extends Component<
  MainContentErrorBoundaryProps,
  MainContentErrorBoundaryState
> {
  state: MainContentErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(error: Error): MainContentErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (process.env.NEXT_PUBLIC_DATADOG_ENABLED === 'true') {
      datadogRum.addError(error, errorInfo);
    }
    console.error('Error caught by main content boundary:', error, errorInfo);
  }

  resetError = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorBoundaryFallback
          error={this.state.error}
          resetError={this.resetError}
        />
      );
    }

    return this.props.children;
  }
}
